/**
 * Labstep
 *
 * @module core/Form/ShowEdit/SearchSelect
 * @desc A showedit component for search select
 */

import { useHasAccess } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { EntityUpdateContainer } from 'labstep-web/containers/Entity/Update';
import { FormShowEditSearchSelectToggle } from 'labstep-web/core/Form/ShowEdit/SearchSelect/Toggle';
import type { APICallOptions } from 'labstep-web/models';
import type {
  FormShowEditSearchSelectProps,
  IFormShowEditSearchSelectContainerProps,
  ParentEntityType,
} from './types';

export const FormShowEditSearchSelectContainer = <
  TParentEntity extends ParentEntityType,
>({
  parentEntity,
  childKeyName,
  childEntityName,
  optimistic = true,
  disabled,
  children,
  childIdAttr = 'id',
}: IFormShowEditSearchSelectContainerProps<TParentEntity>) => {
  const canEdit = useHasAccess(
    parentEntity.entityName,
    parentEntity.idAttr,
    Action.edit,
    childKeyName ?? childEntityName,
  );

  return (
    <EntityUpdateContainer
      entityName={parentEntity.entityName}
      id={parentEntity.idAttr}
      optimistic={optimistic}
    >
      {({ update, status }) => {
        const childKey = childKeyName || childEntityName;
        const value =
          parentEntity[childKey as keyof typeof parentEntity] || null;
        const onChange = (
          option: (typeof parentEntity)[keyof typeof parentEntity],
          options?: APICallOptions,
        ) => {
          update(
            {
              [`${childKey}_id`]: option
                ? option[childIdAttr as keyof typeof option]
                : null,
            },
            options,
          );
        };
        const canEditAndNotDisabled = canEdit && !disabled;
        const hasValue = Boolean(value);
        return children({
          status,
          onChange,
          canEdit: canEditAndNotDisabled,
          hasValue,
          value,
        });
      }}
    </EntityUpdateContainer>
  );
};

export const FormShowEditSearchSelect = <T extends ParentEntityType>({
  parentEntity,
  childEntityName,
  childKeyName,
  optimistic,
  disabled,
  childIdAttr,
  ...rest
}: FormShowEditSearchSelectProps<T>) => (
  <FormShowEditSearchSelectContainer
    parentEntity={parentEntity}
    childKeyName={childKeyName}
    childEntityName={childEntityName}
    optimistic={optimistic}
    disabled={disabled}
    childIdAttr={childIdAttr}
  >
    {({ onChange, canEdit, hasValue, value }) => (
      <FormShowEditSearchSelectToggle
        entityName={childEntityName}
        isTemplate={childKeyName === 'template'}
        hasValue={hasValue}
        value={value}
        canEdit={canEdit}
        onChange={onChange}
        {...rest}
      />
    )}
  </FormShowEditSearchSelectContainer>
);
